import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

import { SimConfirmDialogFn, SimConfirmRequest, SimConfirmResult } from 'sim-management-frontend/dist/sim-management';

/**
 * VMSPRO's confirm dialogs for the `sim-management` library, handed to it as `CONFIRM_DIALOG`.
 *
 * Without this the library draws its own Bootstrap-style modal, which QA flagged against the old
 * screen (VMSPRO-593). The options are the old screen's `promptForReason()` and
 * `refreshMissingSims()` (`sim-usage-management.component.ts` on `master`), so the popups read
 * the same as they did.
 */
export function buildSimConfirmDialog(translate: TranslateService): SimConfirmDialogFn {
	const t = (key: string) => translate.instant(key);

	return async (request: SimConfirmRequest): Promise<SimConfirmResult> => {
		if (request.kind === 'refresh-missing-sims') {
			const result = await Swal.fire({
				text: t('sim.update_sims_confirm'),
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: t('g.ok'),
				cancelButtonText: t('g.cancel')
			});

			return { confirmed: result.isConfirmed };
		}

		const question = `${t('sim.confirmation_massage_1')} ${request.operation} ${t('sim.confirmation_massage_2')}`;
		// No bullets and no indent: SweetAlert centres its text, and a `<ul>` keeps ~40px of
		// padding, which would push the ICCIDs off-centre under the question.
		const list = request.iccids.map((iccid) => `<li>${escapeHtml(iccid)}</li>`).join('');

		const result = await Swal.fire({
			title: t('g.confirmation'),
			html: `<p>${question}</p><ul style="list-style:none;margin:0;padding:0;max-height:180px;overflow:auto">${list}</ul>`,
			icon: 'warning',
			input: 'textarea',
			inputLabel: t('g.reason'),
			inputAttributes: { 'aria-label': t('g.reason') },
			showCancelButton: true,
			confirmButtonText: t('g.confirm'),
			cancelButtonText: t('g.cancel'),
			inputValidator: (value) => (value?.trim() ? null : t('g.field_is_required')),
			// SweetAlert cannot keep Confirm off until the input has text, so it is wired by hand.
			// Spaces or new lines alone keep it off (VMSPRO-592 Summary 2).
			didOpen: () => {
				const confirmButton = Swal.getConfirmButton();
				const input = Swal.getInput();
				if (!confirmButton || !input)
					return;

				confirmButton.disabled = true;
				input.addEventListener('input', () => {
					confirmButton.disabled = !input.value.trim();
				});
			}
		});

		const reason = result.isConfirmed && result.value ? String(result.value).trim() : '';

		return reason ? { confirmed: true, reason } : { confirmed: false };
	};
}

const HTML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** ICCIDs come from device rows, but they are written into `html`, so they are escaped first. */
function escapeHtml(text: string): string {
	return String(text).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}
